// ink-component
import * as React from 'react';
import { Button } from '../Button/Button';
import Icon from '../Icon/Icon';

import ActiveFiltersList from './components/ActiveFiltersList';
import FilterPanel from './components/FilterPanel';
import { ToolbarWrapper } from './components/ToolbarWrapper';
import { SearchListFilter } from './extras/SearchListFilter';
import { AsyncSearchFilter } from './extras/AsyncSearchFilter';
import { DateRangeFilter } from './extras/DateRangeFilter';
import { RadioOptionsFilter } from './extras/RadioOptionsFilter';
import { CashValueFilter } from './extras/CashValueFilter';
import { StickyContainer } from './extras/StickyContainer';

import useOutsideClick from './hooks/useOutsideClick';
import useInternalFilterState from './hooks/useInternalFilterState';
import { FilterType, Filters, NoInfer, FilterChangeHandler } from './types';
import { GlobalFilterContainer, TriggerContainer } from './styles';

export interface GlobalFilterProps<T extends FilterType> {
  /** Unique identifier */
  id?: string;
  /** Filters object definition */
  filters: Filters<T>;
  /** onChange callback; passes individual filter change event and full state  */
  onChange?: FilterChangeHandler<NoInfer<T>>;
  /** Filter state initializer */
  value?: Partial<NoInfer<T>>;
  /** Label for the button that opens the filters panel */
  triggerLabel?: string;
  /** Label for the button that resets all filters */
  resetFiltersLabel?: string;
  /** Filter container width */
  width?: React.CSSProperties['width'];
  /** Start with filters panel open (useful for development) */
  startOpen?: boolean;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
}

/**
 * @purpose Provides multiple filtering criteria in a singular component
 * @status beta
 * @aka filter, multi-filter, filter menu, filter dropdown
 */
export const GlobalFilter = <T extends FilterType>({
  id,
  width = '100%',
  filters,
  triggerLabel = 'Filters',
  resetFiltersLabel = 'Reset',
  value,
  onChange,
  'data-testid': dataTestId,
  ...props
}: GlobalFilterProps<T>) => {
  const manager = useInternalFilterState<T>({ filters, onChange, value, open: props.startOpen });

  const { close } = manager.actions;
  const { open } = manager.state;

  // eslint-disable-next-line ssr-friendly/no-dom-globals-in-react-fc
  const { self: buttonRef, ref: panelRef } = useOutsideClick<HTMLDialogElement, HTMLButtonElement>(
    () => open && close(),
  );

  return (
    <GlobalFilterContainer $width={width} data-testid={dataTestId}>
      <TriggerContainer>
        <Button
          externalRef={buttonRef}
          onClick={manager.actions.toggleOpen}
          aria-expanded={open}
          aria-label="Add filter"
          aria-haspopup="dialog"
          data-testid={dataTestId && `${dataTestId}-trigger-button`}
        >
          {triggerLabel && <>{triggerLabel}&nbsp;</>}
          <Icon type="down-bold" aria-hidden="true" />
        </Button>
        <ActiveFiltersList manager={manager} filters={filters} resetFiltersLabel={resetFiltersLabel} />
      </TriggerContainer>
      {manager.state.open && <FilterPanel id={id} filters={filters} manager={manager} wrapperRef={panelRef} />}
    </GlobalFilterContainer>
  );
};

/** Filter from a list of checkboxes with a search field */
GlobalFilter.SearchListFilter = SearchListFilter;
/** Filter from a list of checkboxes with a search field and async data fetching */
GlobalFilter.AsyncSearchFilter = AsyncSearchFilter;
/** Filter from a date range picker */
GlobalFilter.DateRangeFilter = DateRangeFilter;
/** Filter from a list of radio buttons */
GlobalFilter.RadioOptionsFilter = RadioOptionsFilter;
/** Filter exact decimal values */
GlobalFilter.CashValueFilter = CashValueFilter;

/** Utility to create sticky toolbars */
GlobalFilter.StickyContainer = StickyContainer;
/** Utility for containers of variable width with collapsing children */
GlobalFilter.ToolbarWrapper = ToolbarWrapper;

GlobalFilter.displayName = 'GlobalFilter';

export default GlobalFilter;
